import classNames from "classnames";
import React, {
    useCallback, useEffect, useMemo,
} from "react";
import {
    Chat,
    DashboardSortBy,
    DashboardView, SearchSortBy, TicketView, useSearchAgentChatLazyQuery,
} from "../../../../__generated__/api";
import { cloneWithoutTypename } from "../../../../api";
import { TicketColumnGroup } from "../../../../contexts/ticket-state";
import { useTicketState } from "../../../../contexts/ticket-state/TicketState";
import { SORT_TO_DASHBOARD } from "../../../../contexts/ticket-state/search-state";
import SORT_REGISTRY from "../../../../contexts/ticket-state/sort-by";
import { LoadingIcon } from "../../../loading/Loading";
import ScrollableList from "../../../scrollable-list/ScrollableList";
import ColumnGroup from "../column-group/ColumnGroup";
import Styles from "./Column.module.scss";

const MAX_COLORS = 8;

const SearchColumn = React.forwardRef<HTMLDivElement>((_props: unknown, ref: React.ForwardedRef<HTMLDivElement>) => {
    const ticketContext = useTicketState();

    const search = ticketContext.searchState.useSearchContext();

    const [searchQuery, { data: searchData, loading: isSearchLoading, fetchMore }] = useSearchAgentChatLazyQuery({
        variables: {
            dashboardId: ticketContext.currentDashboard?.id,
            filter: TicketView.MyQueues,
            sortBy: search.sortBy,
            dashboardFilters: search.filters ? cloneWithoutTypename(search.filters) : undefined,
        },
    });

    useEffect(() => {
        ticketContext.searchState.setRemote({
            isLoadingRemote: isSearchLoading,
        });
    }, [isSearchLoading]);

    const handleScroll = useCallback(() => {
        if (searchData?.chats?.nextPageToken) {
            fetchMore({
                variables: {
                    nextPageToken: searchData?.chats?.nextPageToken,
                },
                updateQuery(previous, { fetchMoreResult }) {
                    return {
                        chats: {
                            chats: (previous.chats?.chats ?? []).concat(fetchMoreResult.chats?.chats ?? []).dedupe(row => row.id),
                            nextPageToken: fetchMoreResult.chats?.nextPageToken,
                        },
                        __typename: fetchMoreResult.__typename,
                    };
                },
            });
        }
    }, [searchData?.chats?.nextPageToken]);

    useEffect(() => {
        const hasSearchFilters = (search.filters && Object.values(search.filters).filterNull().isNotEmpty);
        if (search || hasSearchFilters) {
            searchQuery({
                variables: {
                    query: search.search,
                },
            });
            ticketContext.searchState.setRemote({
                isLoadingRemote: true,
                numberRemoteResults: -1,
            });
        }
    }, [search.isSearching, search.search, search.filters]);

    let numericSearch = search.search && /^\d+$/.test(search.search)
        ? parseInt(search.search, 10)
        : undefined;

    if (searchData?.chats?.chats.some(chat => chat.ticketNumber === numericSearch)) {
        numericSearch = undefined;
    }

    const ticketGroup = useMemo(() => {
        const sortBy = search.sortBy && search.sortBy !== SearchSortBy.Relevance
            ? SORT_TO_DASHBOARD[search.sortBy]
            : undefined;

        const SortBy = sortBy ? SORT_REGISTRY.getAll().find(sorter => sorter.shouldCreate(sortBy)) : undefined;
        const group = new TicketColumnGroup(
            undefined,
            SortBy ? new SortBy(sortBy as DashboardSortBy) : undefined,
        );
        searchData?.chats?.chats.forEach(chat => {
            const ticket = ticketContext.tickets.get(chat.id);
            if (!ticket) {
                const ticketItem = ticketContext.tickets.create(chat as Chat);
                ticketItem.setIsArchived(true);
                group.addTicket(ticketItem);
            }
        });
        ticketContext.searchState.setRemote({
            isLoadingRemote: false,
            group,
            numberRemoteResults: group.tickets.length,
            numericSearch,
        });
        return group;
    }, [searchData, numericSearch]);

    const view = ticketContext.currentDashboard?.settings?.view ?? DashboardView.Kanban;

    return !isSearchLoading && search.numberRemoteResults <= 0 ? undefined : (
        <div
            ref={ref}
            className={classNames(
                "column",
                Styles.column,
                Styles[`column-${view?.toLowerCase()}`],
            )}
        >
            <div className={classNames("column-header", `color-${(0 % MAX_COLORS) + 1}`, "d-flex")}>
                <div
                    className="flex-grow-1 align-items-center d-flex text-body-1-medium column-title p3e-2"
                    title="Archived Tickets"
                >
                    <span className="column-text">
                        Archived
                    </span>
                    {" "}
                    {search.numberRemoteResults >= 0 && (
                        <span className="ticket-count">{search.numberRemoteResults}</span>
                    )}

                </div>

            </div>

            <div className="column-body">
                <div className="column-body-contents">
                    <ScrollableList onScroll={handleScroll}>
                        <ColumnGroup draggingItem={undefined} group={ticketGroup} />
                        {isSearchLoading && (<LoadingIcon className={Styles.loading} />)}
                    </ScrollableList>
                </div>

            </div>

        </div>
    );
});
export default SearchColumn;
